import { FC, useMemo, useState } from 'react';
import { DateValueType, formatDate } from '@lib/utils.ts';
import {
  Button,
  Card,
  DateRangePicker,
  Divider,
  RadixTable,
  TColumn,
} from '@features/ui';
import {
  TExpressPAAccountRefundData,
  TExpressPAAccountRefundParams,
} from '@src/types';
import { getExpressPAAccountRefund } from '@src/services/reports.ts';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';

export const ExpressPAAccountRefunds: FC = () => {
  const navigate = useNavigate();
  const [createdOn, setCreatedOn] = useState<DateValueType>(null);
  const [data, setData] = useState<TExpressPAAccountRefundData[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  const fetchData = async (params?: TExpressPAAccountRefundParams) => {
    if (!params?.created_on) return;

    setIsLoading(true);
    try {
      const res = await getExpressPAAccountRefund(params);

      setData(res.data);
      if (res.data.length === 0) {
        toast.warn('No data found for the selected date');
        console.warn('No data found for the selected date');
      }
    } catch (error) {
      console.error('Error fetching Express PA Account Refunds:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const columns = useMemo(
    (): TColumn<TExpressPAAccountRefundData>[] => [
      {
        title: 'Username',
        render: (item) => (
          <button
            className="text-blue hover:underline cursor-pointer transition-colors"
            onClick={() => navigate(`/admin/users/${item?.user_id}`)}
          >
            {item?.username}
          </button>
        ),
      },
      {
        title: 'Express Account Name',
        render: (item) => <p>{item?.express_account_name}</p>,
      },
      {
        title: 'PA Account Name',
        render: (item) => <p>{item?.pa_account_name}</p>,
      },
      {
        title: 'Invoice ID',
        render: (item) => <p>{item?.invoice_id}</p>,
      },
      {
        title: 'Amount',
        render: (item) => <p>${item?.amount}</p>,
      },
      {
        title: 'Refunded Amount',
        render: (item) => <p>${item?.refunded_amount}</p>,
      },
      {
        title: 'Refunded On',
        render: (item) => <p>{formatDate(item?.refunded_on)}</p>,
      },
    ],
    [navigate]
  );

  const handleGetData = async () => {
    if (!createdOn?.startDate) {
      console.warn('Please select a date');

      return;
    }

    const formattedDate = createdOn.startDate.toISOString().split('T')[0]; // YYYY-MM-DD

    await fetchData({ created_on: formattedDate });
  };

  return (
    <div>
      <div className="flex justify-between lg:items-center gap-7.5 lg:flex-row flex-col">
        <h1 className="text-[23px]">Express Account - PA Refunds</h1>
      </div>
      <Card className="mt-4.5">
        <div className="flex justify-between items-start lg:flex-row flex-col mb-5 gap-6">
          <div className="w-full flex gap-6 flex-wrap">
            <DateRangePicker
              asSingle={true}
              className="max-w-[239px]"
              placeholder="Created On"
              size="sm"
              value={createdOn}
              variant="outline"
              onChange={setCreatedOn}
            />
          </div>
          <div className="flex gap-5">
            <Button
              disabled={isLoading}
              size="sm"
              variant="lime"
              onClick={handleGetData}
            >
              {isLoading ? 'Loading...' : 'Get'}
            </Button>
          </div>
        </div>
        <Divider />
        <RadixTable columns={columns} data={data} />
      </Card>
    </div>
  );
};
